// context/AppContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AuthService from '../services/auth';
import SpotifyService from '../services/spotify';
import FavoritesService from '../services/favorites';
import { supabase } from '../config/supabase';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [favoriteArtistIds, setFavoriteArtistIds] = useState([]);

    // Carica preferiti quando l'utente è loggato
    const loadFavorites = useCallback(async () => {
        try {
            const ids = await FavoritesService.getFavoriteArtistIds();
            setFavoriteArtistIds(ids);
        } catch (error) {
            console.warn('[AppContext] Error loading favorites:', error);
        }
    }, []);

    // Carica lo stato iniziale (NON bloccante - lazy loading)
    useEffect(() => {
        const checkAuth = async () => {
            try {
                // Verifica la sessione corrente
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) {
                    console.warn('[AppContext] Error getting session:', sessionError.message);
                }

                if (session?.user) {
                    console.log('[AppContext] ✅ User session found:', session.user.email);
                    setUser(session.user);
                    setIsAuthenticated(true);
                } else {
                    console.log('[AppContext] ⚠️ No active session');
                    setUser(null);
                    setIsAuthenticated(false);
                }

                // Check rapido del token UTENTE (non client credentials)
                // /me richiede SEMPRE un token utente, non può usare client credentials
                const isUserAuth = AuthService.isUserAuthenticated();
                
                if (isUserAuth) {
                    // Token UTENTE presente, prova a caricare il profilo IN BACKGROUND
                    // NON bloccare l'app aspettando il profilo!
                    SpotifyService.getUserProfile()
                        .then(profile => {
                            setUserProfile(profile);
                            setIsAuthenticated(true);
                        })
                        .catch(error => {
                            // Se è 401, il token è scaduto/invalido - pulisci e ferma
                            if (error.message === 'Authentication required' || error.message.includes('401')) {
                                console.warn('Profile load failed (background): User token expired/invalid');
                                setIsAuthenticated(false);
                                setUserProfile(null);
                                AuthService.clearAllData();
                            } else {
                                // Altri errori (es. rete) - mantieni stato ma senza profilo
                                console.warn('Profile load failed (background):', error.message);
                                setIsAuthenticated(false);
                                setUserProfile(null);
                            }
                        });
                } else {
                    // Nessun token utente valido - non chiamare /me
                    setIsAuthenticated(false);
                    setUserProfile(null);
                }
                
                // Pre-carica il token Client Credentials in background per evitare cold start
                // quando l'utente apre la prima scheda artista
                AuthService.getClientCredentialsToken()
                    .then(token => {
                        if (token) {
                            console.log('✅ [App] Client Credentials token pre-loaded successfully');
                        }
                    })
                    .catch(error => {
                        // Ignora errori silenziosamente - non bloccare l'app
                        console.warn('⚠️ [App] Failed to pre-load Client Credentials token:', error.message);
                    });
            } catch (error) {
                console.error('[AppContext] Error in checkAuth:', error);
            } finally {
                // Sblocca immediatamente l'app (non aspettare il profilo)
                setLoading(false);
            }
        };

        // Controlla subito la sessione
        checkAuth();
    }, []);

    useEffect(() => {
        // Listener per cambiamenti di autenticazione (gestisce anche il callback OAuth)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('[AppContext] 🔐 Auth state changed:', event, session?.user?.email || 'no user');
            
            if (session?.user) {
                console.log('[AppContext] ✅ User authenticated:', session.user.email);
                setUser(session.user);
                setIsAuthenticated(true);
                
                // Carica preferiti quando l'utente è loggato
                loadFavorites();
                
                // Se è un evento di SIGNED_IN, ripristina lo state salvato e pulisci l'URL
                if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                    // Piccolo delay per assicurarsi che tutto sia salvato
                    setTimeout(() => {
                        // Ripristina lo state salvato prima del login
                        const savedState = sessionStorage.getItem('pending_login_redirect');
                        if (savedState) {
                            try {
                                const state = JSON.parse(savedState);
                                console.log('[AppContext] 🔄 Restoring state after login:', state);
                                
                                // Naviga alla pagina salvata se diversa da quella corrente
                                const currentPath = window.location.pathname + window.location.search;
                                const savedPath = state.pathname + (state.search || '');
                                
                                if (currentPath !== savedPath) {
                                    console.log('[AppContext] 🧭 Navigating to saved path:', savedPath);
                                    // Se c'è uno state di router salvato, non usare window.location.href
                                    // ma lascia che React Router gestisca la navigazione
                                    if (state.routerState) {
                                        console.log('[AppContext] 📦 Router state will be restored by ShowResult component');
                                        // Non fare redirect qui, lascia che ShowResult ripristini lo state
                                    } else {
                                        window.location.href = savedPath;
                                    }
                                    sessionStorage.removeItem('pending_login_redirect');
                                    return; // Esci subito
                                }
                                
                                sessionStorage.removeItem('pending_login_redirect');
                            } catch (e) {
                                console.warn('[AppContext] Failed to restore state:', e);
                                sessionStorage.removeItem('pending_login_redirect');
                            }
                        }
                        
                        // Pulisci parametri OAuth dall'URL
                        const url = new URL(window.location.href);
                        let shouldUpdate = false;
                        
                        // Rimuovi parametri OAuth dalla query string
                        ['code', 'state', 'error', 'error_description', 'error_code'].forEach(param => {
                            if (url.searchParams.has(param)) {
                                url.searchParams.delete(param);
                                shouldUpdate = true;
                            }
                        });
                        
                        // Rimuovi hash OAuth
                        if (window.location.hash && (window.location.hash.includes('access_token') || window.location.hash.includes('error'))) {
                            window.location.hash = '';
                            shouldUpdate = true;
                        }
                        
                        if (shouldUpdate) {
                            window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
                        }
                    }, 100);
                }
            } else {
                console.log('[AppContext] ⚠️ User signed out');
                setUser(null);
                setIsAuthenticated(false);
                setUserProfile(null);
                setFavoriteArtistIds([]);
                AuthService.clearAllData();
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [loadFavorites]);

    // Aggiorna preferiti dopo aggiunta/rimozione
    const refreshFavorites = useCallback(async () => {
        await loadFavorites();
    }, [loadFavorites]);

    // Login handler
    const handleLogin = useCallback(async () => {
        if (typeof window === 'undefined') {
            return;
        }
        
        try {
            // Salva lo state corrente PRIMA del redirect per preservarlo dopo il login
            // Include anche lo state di React Router se disponibile (per daily-discovery-artists, etc.)
            const currentState = {
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                timestamp: Date.now()
            };
            
            // Prova a salvare lo state di React Router se siamo su una route che lo usa
            // Lo state viene perso durante il redirect, quindi lo salviamo in sessionStorage
            const routesWithState = ['/daily-discovery-artists', '/similar', '/random', '/top-artists'];
            if (routesWithState.some(route => window.location.pathname.startsWith(route))) {
                // Salva anche informazioni per ricostruire lo state dopo il login
                const routerStateKey = `pending_router_state_${window.location.pathname}`;
                const existingState = sessionStorage.getItem(routerStateKey);
                if (existingState) {
                    currentState.routerState = JSON.parse(existingState);
                    console.log('[AppContext] 💾 Found existing router state to preserve');
                }
            }
            
            sessionStorage.setItem('pending_login_redirect', JSON.stringify(currentState));
            console.log('[AppContext] 💾 Saved state before login:', currentState);

            // Torna esattamente alla pagina corrente (mantiene persistenze/slide)
            const { origin, pathname, search } = window.location;
            const redirectTo = `${origin}${pathname}${search}`;

            console.log('[AppContext] 🔐 Initiating Google OAuth login, redirect to:', redirectTo);

            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    }
                }
            });

            if (error) {
                console.error('[AppContext] ❌ OAuth login error:', error.message);
                sessionStorage.removeItem('pending_login_redirect');
                throw error;
            }

            // Il redirect avviene automaticamente, non serve fare nulla qui
            console.log('[AppContext] ✅ OAuth redirect initiated');
        } catch (error) {
            console.error('[AppContext] ❌ Login failed:', error);
            sessionStorage.removeItem('pending_login_redirect');
            // Potresti voler mostrare un messaggio di errore all'utente qui
        }
    }, []);

    // Logout handler
    const handleLogout = useCallback(async () => {
        try {
            await supabase.auth.signOut();
        } catch (error) {
            console.warn('Supabase signOut error:', error.message);
        } finally {
            AuthService.logout();
            setIsAuthenticated(false);
            setUser(null);
            setUserProfile(null);
        }
    }, []);

    // Event listener per logout
    useEffect(() => {
        const handleAuthError = () => {
            setIsAuthenticated(false);
            setUserProfile(null);
        };

        window.addEventListener('auth_error', handleAuthError);
        window.addEventListener('auth_logout', handleAuthError);

        return () => {
            window.removeEventListener('auth_error', handleAuthError);
            window.removeEventListener('auth_logout', handleAuthError);
        };
    }, []);

    const value = {
        isAuthenticated,
        user,
        userProfile,
        loading,
        favoriteArtistIds,
        refreshFavorites,
        login: handleLogin,
        logout: handleLogout
    };

    // Non bloccare mai l'app - mostra sempre il contenuto
    // Il caricamento del profilo avviene in background
    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within AppProvider');
    }
    return context;
};